<template>
  <div>
    <ul v-if="errors.length > 0" class="mb-5 text-brand-500">
      <li v-for="error in errors" :key="error" v-text="error"></li>
    </ul>

    <div v-if="groupOptions !== undefined" class="mb-6">
      <p class="mb-2">{{ $t('activity.group') }}:</p>
      <options :values="groupOptions" :name="groupName" v-model="group"></options>

      <div v-if="group !== null && activityTypes !== undefined">
        <p class="mt-6 mb-2">{{ $t('activity.type') }}:</p>
        <options :values="availableTypes" v-model="type"></options>
      </div>

      <input type="hidden" name="group_id" :value="selectedGroupId" />
      <input type="hidden" name="activity_type_id" :value="selectedTypeId" />
    </div>

    <input
      type="hidden"
      name="duration_type"
      :value="activityDate ? activityDate.type.key : null"
    />

    <div v-if="type">
      <p class="mb-2">{{ $t('activity.form.duration') }}:</p>
      <activity-date-input
        :types="[
          { key: 'part', name: this.$t('activity.form.part_of_day') },
          { key: 'whole', name: this.$t('activity.form.whole_day') },
          { key: 'multiple', name: this.$t('activity.form.multiple_days') },
        ]"
        :default-date="defaultDate"
        :default-type="val('duration_type')"
        :additional-times="type ? type.has_additional_times : false"
        :hide-recurrence="existingGroup !== undefined"
        v-model="activityDate"
      ></activity-date-input>
    </div>

    <div v-show="type">
      <label class="mt-6 flex flex-col">
        <span class="mb-2 block">{{ $t('activity.title') }}:</span>
        <input
          type="text"
          class="form-input md:w-1/2"
          name="title"
          v-model="title"
          maxlength="100"
        />
      </label>

      <label class="mt-6 flex flex-col" for="location">
        <span class="mb-2 block">{{ $t('activity.location') }}:</span>
        <input
          type="text"
          class="form-input w-full md:w-1/2"
          name="location"
          v-model="location"
          v-if="!googleMaps"
        />
        <div v-if="googleMaps && !location_place_id">
          <vue-google-autocomplete
            ref="googleLocation"
            id="location"
            name="location"
            classname="form-input w-full md:w-1/2"
            :types="null"
            placeholder=""
            v-model="location"
            v-on:placechanged="getAddressData"
            v-if="showGoogleMaps"
          >
          </vue-google-autocomplete>
          <input
            type="text"
            name="location"
            v-model="location"
            v-else
            class="form-input w-full md:w-1/2"
            @focus="activateGoogleAutocomplete"
          />
        </div>
        <div class="flex md:w-1/2" v-if="googleMaps && location_place_id">
          <span class="form-input flex-1 rounded-r-none border-r-0" v-text="location"></span>
          <button
            type="button"
            class="form-input rounded-l-none border-l-0 hover:text-brand-500"
            @click="resetLocation"
          >
            <span class="fal fa-times px-1"></span>
          </button>
          <input type="hidden" name="location" v-model="location" v-if="location_place_id != -1" />
          <input
            type="hidden"
            name="place_identifier"
            v-model="location_place_id"
            v-if="location_place_id != -1"
          />
        </div>
      </label>

      <div class="mt-6 flex flex-col">
        <span class="mb-2 block">{{ $t('activity.description') }}:</span>
        <tiptap ref="tiptap" class="lg:w-3/4" v-model="description" />
        <input type="hidden" name="description" :value="JSON.stringify(description)" />
      </div>

      <div v-if="group">
        <label class="mt-6 flex flex-col" v-for="{ id, name } in group.custom_properties" :key="id">
          <span class="mb-2 block">{{ name }}:</span>
          <textarea
            ref="property_textarea"
            class="form-textarea min-h-24 lg:w-3/4"
            :name="'properties[' + id + ']'"
            v-model="custom_properties[id]"
          ></textarea>
        </label>

        <div class="mt-6 flex flex-col" v-if="type && type.has_participants && group.participants">
          <span class="mb-2 block">{{ $t('activity.form.participants') }}:</span>
          <div class="form-input flex flex-wrap overflow-hidden py-0 text-base lg:w-3/4">
            <div class="w-full sm:columns-2" ref="participants-container">
              <div
                class="flex flex-col py-3 pr-4"
                v-for="(possibleParticipants, group) in group.participants"
                :key="group"
              >
                <h3 class="mb-1 font-semibold" v-text="group"></h3>
                <div
                  v-for="{ id, name, is_stand_in, is_invitation } in possibleParticipants"
                  :key="id"
                  class="center flex space-x-1"
                >
                  <label
                    class="flex items-center space-x-2 pr-1"
                    :class="{ 'text-gray-500': is_stand_in }"
                  >
                    <input
                      type="checkbox"
                      name="participants[]"
                      :value="id"
                      class="form-checkbox"
                      v-model="participants"
                    />
                    <span v-text="name"></span>
                  </label>
                  <icon-badge
                    class="border-brand-500 bg-brand-100 text-brand-500"
                    icon-class="fas fa-exclamation"
                    v-if="is_invitation"
                  >
                    {{ $t('activity.pending_invitation') }}
                  </icon-badge>
                  <state-badge
                    v-if="participantStates[id]"
                    :state="participantStates[id].state"
                    :system-state="participantStates[id].systemState"
                  ></state-badge>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-1 text-sm text-gray-500">
            {{ $t('global.select') }}:
            <span
              class="cursor-pointer select-none select-all hover:text-black"
              v-text="this.$t('global.all')"
              @click="selectAllParticipants"
            ></span>
            /
            <span
              class="cursor-pointer select-none hover:text-black"
              v-text="this.$t('global.none')"
              @click="selectNoParticipants"
            ></span>
          </div>

          <label class="mt-6 flex items-center">
            <input
              type="checkbox"
              name="requires_approval"
              value="1"
              class="form-checkbox"
              v-model="requires_approval"
            />
            <span class="ml-2" v-text="this.$t('activity.form.requires_approval')"></span>
          </label>

          <label class="mt-2 flex items-center" v-if="notifyParticipants">
            <input
              type="checkbox"
              name="notify_participants"
              value="1"
              class="form-checkbox"
              checked
            />
            <span class="ml-2" v-text="this.$t('activity.form.notify_participants')"></span>
          </label>
        </div>
      </div>
    </div>

    <div class="mt-6 flex flex-wrap items-center" v-if="showFooter">
      <slot />
    </div>
  </div>
</template>

<script>
import VueGoogleAutocomplete from 'vue-google-autocomplete'
import GrowingTextarea from '../classes/GrowingTextarea'
import { nextTick, ref } from 'vue'
import Tiptap from './Tiptap'

export default {
  components: { Tiptap, VueGoogleAutocomplete },
  props: {
    activityTypes: {
      type: Array,
    },
    groups: {
      type: Array,
    },
    existingGroup: {
      type: Object,
    },
    old: {
      default: {},
    },
    errors: {
      default: {},
    },
    hasPlace: {
      type: Boolean,
      default: false,
    },
    googleMaps: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      group: null,
      type: null,
      activityDate: null,
      title: null,
      description: null,
      location: null,
      location_place_id: null,
      custom_properties: {},
      participants: [],
      participantStates: {},
      requires_approval: false,
      defaultDate: null,
      showGoogleMaps: false,
      propertyTextareaRefs: [],
    }
  },
  computed: {
    selectedGroupId() {
      if (!this.group) {
        return null
      }
      return this.group.id
    },
    selectedTypeId() {
      if (!this.type) {
        return null
      }
      return this.type.id
    },
    groupOptions() {
      if (this.groups === undefined) {
        return undefined
      }

      let groups = this.groups
      groups.unshift(false)

      return groups
    },
    allowsUser() {
      if (!this.activityType) {
        return false
      }

      return this.activityType.available_for_users
    },
    availableTypes() {
      if (this.activityTypes === undefined) {
        return undefined
      }

      return this.activityTypes.filter((type) => {
        if (!this.group) {
          return type.available_for_users
        }

        return this.group.activity_types.includes(type.id)
      })
    },
    notifyParticipants() {
      if (!this.group) {
        return false
      }
      return this.group.notify_participants
    },
    showFooter() {
      return (
        (this.groupOptions === undefined || this.group !== null) &&
        (this.availableTypes === undefined || this.type !== null)
      )
    },
  },
  methods: {
    setPropertyTextareaRef(el) {
      if (!el) {
        return
      }

      this.propertyTextareaRefs.push(el)
    },
    selectAllParticipants() {
      if (!this.group || !this.group.participants) {
        return
      }

      this.participants = Object.entries(this.group.participants).reduce(
        (array, [group, participants]) => {
          return array.concat(participants.filter((p) => !p.is_stand_in).map((p) => p.id))
        },
        []
      )
    },
    selectNoParticipants() {
      this.participants = []
    },
    groupName(group) {
      if (group === false) {
        return this.$t('activity.no_group')
      }
      return group.name
    },
    isSelectedType(type) {
      if (!this.activityType) {
        return false
      }

      return this.activityType.id === type.id
    },
    val(name, fallback = null) {
      if (this.old === null || this.old[name] === undefined) {
        return fallback || false
      }
      return this.old[name]
    },
    getAddressData(addressData, placeResultData, id) {
      let name = null
      if (
        placeResultData.name != undefined &&
        placeResultData.name != '' &&
        !placeResultData.formatted_address.includes(placeResultData.name)
      ) {
        name = placeResultData.name
      }

      this.location = (name ? name + ', ' : '') + placeResultData.formatted_address
      this.location_place_id = placeResultData.place_id
    },
    resetLocation() {
      this.location = null
      this.location_place_id = null
    },
    activateGoogleAutocomplete() {
      this.showGoogleMaps = true
      nextTick(() => {
        this.$refs.googleLocation.$refs.autocomplete.focus()
        this.$refs.googleLocation.$refs.autocomplete.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
          }
        })
      })
    },
  },

  watch: {
    group(group) {
      nextTick(() => {
        if (group.custom_properties !== undefined) {
          group.custom_properties.forEach(({ id, default_value }) => {
            if (!default_value) {
              return
            }

            if (this.custom_properties[id] !== undefined) {
              return
            }

            this.custom_properties[id] = default_value
          })

          this.propertyTextareaRefs.forEach((textarea) => {
            textarea.oninput = () => {
              GrowingTextarea.grow(textarea)
            }
            GrowingTextarea.grow(textarea)
          })
        }
      })
    },
  },

  mounted() {
    if (this.existingGroup !== undefined) {
      this.group = this.existingGroup
    }

    let groupId = this.val('group_id')
    if (groupId !== false) {
      if (groupId) {
        this.group = this.groups.find((t) => {
          return t.id === parseInt(groupId)
        })
      } else {
        this.group = false
      }
    }

    let activityTypeId = this.val('activity_type_id')
    if (activityTypeId !== false && activityTypeId) {
      this.type = this.activityTypes.find((t) => {
        return t.id === parseInt(activityTypeId)
      })
    }

    nextTick(() => {
      if (this.activityDate) {
        this.activityDate.recurring = this.val('recurring') || ''
        this.activityDate.startDate = this.val('start_date') || null
        this.activityDate.endDate = this.val('end_date') || null
        this.activityDate.startTime = this.val('start_time') || null
        this.activityDate.endTime = this.val('end_time') || null
        this.activityDate.presentTime = this.val('present_time') || null
        this.activityDate.soundcheckTime = this.val('soundcheck_time') || null
      }

      this.defaultDate = this.val('date') || null
      this.title = this.val('title') || null

      this.description = this.val('description') || null
      this.$refs['tiptap'].updateValue(this.description)

      this.location = this.val('location') || null
      if (this.location && this.hasPlace) {
        this.location_place_id = -1
      }

      this.custom_properties = this.val('properties') || {}

      let oldParticipants = this.val('participants')
      this.participants = oldParticipants ? Object.keys(oldParticipants) : []

      this.participantStates = {}
      Object.keys(oldParticipants).forEach((key) => {
        let participantState = oldParticipants[key]
        if (participantState && participantState !== true) {
          this.participantStates[key] = participantState
        }
      })

      this.requires_approval = this.val('requires_approval') || null

      nextTick(() => {
        this.$el.querySelectorAll('textarea.growing').forEach((e) => GrowingTextarea.grow(e))
      })
    })
  },
  beforeUpdate() {
    this.propertyTextareaRefs = []
  },
}
</script>
